/**
 * DEV-1921. Обучение конструктору воронки: общие подписи коучмарк-тура
 * (`common.onboarding.*`, их берут все разделы с туром) и словарь тем
 * конструктора (`pipeline.help.*`).
 *
 * ⚠ В строках нельзя литеральные `{`, `}` и `@`: vue-i18n читает их как
 * плейсхолдер и linked-message и падает на сборке.
 */

// Навигация тура и чужие подписи «?» — общие для аналитики ЖК, модерации и конструктора.
const ruOnboarding = {
  progress: '{current} из {total}',
  skip: 'Пропустить',
  back: 'Назад',
  next: 'Далее',
  done: 'Готово',
  topicOf: 'Тема {current} из {total}',
  topicsCount: '{n} тема | {n} темы | {n} тем',
  good: 'Так хорошо',
  weak: 'Так слабо',
  showOnScreen: 'Показать на экране →',
}

const enOnboarding = {
  progress: '{current} of {total}',
  skip: 'Skip',
  back: 'Back',
  next: 'Next',
  done: 'Done',
  topicOf: 'Topic {current} of {total}',
  topicsCount: '{n} topic | {n} topics',
  good: 'Good example',
  weak: 'Weak example',
  showOnScreen: 'Show on screen →',
}

const vnOnboarding = {
  progress: '{current}/{total}',
  skip: 'Bỏ qua',
  back: 'Quay lại',
  next: 'Tiếp',
  done: 'Xong',
  topicOf: 'Chủ đề {current}/{total}',
  topicsCount: '{n} chủ đề',
  good: 'Nên như vậy',
  weak: 'Còn yếu',
  showOnScreen: 'Xem trên màn hình →',
}

const ruHelp = {
  title: 'Как это работает',
  intro:
    'Конструктор задаёт правила, по которым сделка идёт по воронке. Ниже — темы обучения: откройте нужную или пройдите весь тур целиком.',
  button: 'Обучение',
  start: 'Начать обучение',
  show: 'Показать',
  replay: 'Пройти обучение заново',
  hideHints: 'Скрыть подсказки',
  showHints: 'Показывать подсказки',
  welcomeTitle: 'Конструктор воронки стал богаче',
  welcomeText:
    'Теперь этап умеет отмечать успех и провал, пропускать проверку задач и требовать документы, а типы документов описывают, что именно проверяет ИИ. Пройдите короткое обучение — десять тем, каждую можно открыть отдельно.',
  topics: {
    stageOrder: {
      title: 'Этапы и порядок',
      description:
        'Порядок этапов здесь — это порядок колонок в канбане. Перетащите этап за ручку слева, чтобы поменять их местами.',
    },
    stageType: {
      title: 'Тип этапа: успех и провал',
      description:
        '«Успех» закрывает сделку выигранной, «Провал» — проигранной с причиной. У системных этапов тип не меняется: иначе воронка осталась бы без финала.',
    },
    stageSkip: {
      title: 'Пропуск проверки задач',
      description:
        'Тумблер снимает блокировку по незавершённым задачам при переходе на этот этап. Открытые задачи покинутого этапа при этом закрываются.',
    },
    stageTasks: {
      title: 'Задачи этапа',
      description:
        'Шаблоны создают задачи автоматически, когда сделка входит на этап. Обязательные не дают уйти дальше, пока их не закроют.',
    },
    stageDocs: {
      title: 'Обязательные документы этапа',
      description:
        'Список документов, без которых на этап не перейти. Проверяется только при движении вперёд: откат назад документы не держат.',
    },
    stageClientFields: {
      title: 'Обязательные поля клиента',
      description:
        'Поля карточки клиента, без которых на этап не перейти: пока они пустые, сделка стоит. Проверяется только при движении вперёд.',
    },
    docTypes: {
      title: 'Типы документов',
      description:
        'Общий справочник для всей воронки: что за документ и как его проверять. Один тип можно требовать сразу на нескольких этапах.',
    },
    docDescription: {
      title: 'Описание для ИИ',
      description:
        'Опишите документ так, как объяснили бы новому сотруднику: что это, какие реквизиты обязаны быть читаемы и что считать браком. Формат файла и строгость задаются отдельными полями.',
      good: 'Разворот паспорта с фотографией. Должны читаться ФИО, серия и номер, дата выдачи и кем выдан. Скан целиком, без обрезанных краёв и бликов.',
      weak: '«Паспорт» — с таким описанием ИИ подтвердит почти любой документ с фотографией.',
    },
    docFields: {
      title: 'Извлекаемые поля',
      description:
        'Какие данные ИИ достанет из файла. Если обязательное поле осталось пустым — документ отклоняется, даже когда всё остальное в порядке.',
    },
    docThreshold: {
      title: 'Порог уверенности и статусы',
      description:
        'Ниже порога документ получает «Отклонён ИИ», и менеджер видит причины. Руководитель может подтвердить документ вручную вопреки решению ИИ.',
    },
    taskTypes: {
      title: 'Типы задач',
      description:
        'Чем задача является по существу: звонок, касание, встреча. Тип необязателен, но по нему считается работа и фильтруются списки. Системный тип нельзя удалить — только выключить, и он останется на уже заведённых задачах.',
    },
  },
}

const enHelp = {
  title: 'How it works',
  intro:
    'The constructor sets the rules a deal follows through the funnel. Below are the training topics: open the one you need or take the whole tour.',
  button: 'Guide',
  start: 'Start the guide',
  show: 'Show',
  replay: 'Take the guide again',
  hideHints: 'Hide hints',
  showHints: 'Show hints',
  welcomeTitle: 'The funnel constructor got richer',
  welcomeText:
    'A stage can now mark a win or a loss, skip the task check and require documents, while document types describe what exactly the AI verifies. Take the short guide — ten topics, each can be opened on its own.',
  topics: {
    stageOrder: {
      title: 'Stages and their order',
      description:
        'The order here is the order of columns on the board. Drag a stage by the handle on the left to move it.',
    },
    stageType: {
      title: 'Stage type: win and loss',
      description:
        '“Win” closes the deal as won, “Loss” as lost with a reason. System stages keep their type — otherwise the funnel would lose its final stage.',
    },
    stageSkip: {
      title: 'Skipping the task check',
      description:
        'The switch lifts the block on unfinished tasks when a deal moves to this stage. Open tasks of the stage being left are closed.',
    },
    stageTasks: {
      title: 'Stage tasks',
      description:
        'Templates create tasks automatically when a deal enters the stage. Mandatory ones hold the deal until they are done.',
    },
    stageDocs: {
      title: 'Documents required by the stage',
      description:
        'Documents without which the deal cannot enter the stage. Checked only when moving forward: going back is never blocked.',
    },
    stageClientFields: {
      title: 'Required client fields',
      description:
        'Client card fields without which the deal cannot enter the stage: while they are empty, the deal waits. Checked when moving forward only.',
    },
    docTypes: {
      title: 'Document types',
      description:
        'A directory shared by the whole funnel: what the document is and how to verify it. One type can be required by several stages.',
    },
    docDescription: {
      title: 'Description for the AI',
      description:
        'Describe the document the way you would explain it to a new colleague: what it is, which details must be legible and what counts as unusable. File format and strictness are set by separate fields.',
      good: 'The passport spread with the photo. Full name, series and number, issue date and issuing authority must be legible. The whole scan, no cropped edges or glare.',
      weak: '“Passport” — with a description like that the AI will approve almost any document with a photo.',
    },
    docFields: {
      title: 'Extracted fields',
      description:
        'The data the AI pulls out of the file. If a required field stays empty the document is rejected, even when everything else is fine.',
    },
    docThreshold: {
      title: 'Confidence threshold and statuses',
      description:
        'Below the threshold the document is “Rejected by AI” and the manager sees the reasons. A supervisor can approve it manually despite the AI verdict.',
    },
    taskTypes: {
      title: 'Task types',
      description:
        'What a task is in essence: a call, a touch, a meeting. The type is optional, but work is counted and lists are filtered by it. A system type cannot be deleted — only switched off, and it stays on existing tasks.',
    },
  },
}

const vnHelp = {
  title: 'Cách hoạt động',
  intro:
    'Trình dựng phễu đặt ra quy tắc mà giao dịch đi theo. Bên dưới là các chủ đề hướng dẫn: mở chủ đề cần thiết hoặc xem toàn bộ.',
  button: 'Hướng dẫn',
  start: 'Bắt đầu hướng dẫn',
  show: 'Xem',
  replay: 'Xem lại hướng dẫn',
  hideHints: 'Ẩn gợi ý',
  showHints: 'Hiện gợi ý',
  welcomeTitle: 'Trình dựng phễu có thêm khả năng mới',
  welcomeText:
    'Giai đoạn giờ có thể đánh dấu thắng và thua, bỏ qua kiểm tra công việc và yêu cầu tài liệu, còn loại tài liệu mô tả chính xác điều AI kiểm tra. Hãy xem hướng dẫn ngắn — mười chủ đề, mỗi chủ đề mở riêng được.',
  topics: {
    stageOrder: {
      title: 'Giai đoạn và thứ tự',
      description:
        'Thứ tự ở đây chính là thứ tự cột trên bảng. Kéo giai đoạn bằng tay cầm bên trái để đổi chỗ.',
    },
    stageType: {
      title: 'Loại giai đoạn: thắng và thua',
      description:
        '«Thành công» đóng giao dịch là thắng, «Thất bại» là thua kèm lý do. Giai đoạn hệ thống giữ nguyên loại, nếu không phễu sẽ mất giai đoạn cuối.',
    },
    stageSkip: {
      title: 'Bỏ qua kiểm tra công việc',
      description:
        'Công tắc gỡ chặn theo công việc chưa xong khi chuyển sang giai đoạn này. Công việc đang mở của giai đoạn rời đi sẽ được đóng.',
    },
    stageTasks: {
      title: 'Công việc của giai đoạn',
      description:
        'Mẫu tự tạo công việc khi giao dịch vào giai đoạn. Công việc bắt buộc giữ giao dịch lại cho tới khi hoàn thành.',
    },
    stageDocs: {
      title: 'Tài liệu bắt buộc của giai đoạn',
      description:
        'Những tài liệu mà thiếu chúng thì không vào được giai đoạn. Chỉ kiểm tra khi tiến lên: lùi lại không bao giờ bị chặn.',
    },
    stageClientFields: {
      title: 'Trường bắt buộc của khách hàng',
      description:
        'Các trường trong hồ sơ khách hàng mà thiếu chúng thì không vào được giai đoạn. Chỉ kiểm tra khi tiến lên.',
    },
    docTypes: {
      title: 'Loại tài liệu',
      description:
        'Danh mục dùng chung cho cả phễu: tài liệu là gì và kiểm tra thế nào. Một loại có thể được yêu cầu ở nhiều giai đoạn.',
    },
    docDescription: {
      title: 'Mô tả cho AI',
      description:
        'Hãy mô tả tài liệu như khi giải thích cho nhân viên mới: đó là gì, thông tin nào bắt buộc phải đọc được và thế nào là không đạt. Định dạng tệp và độ nghiêm ngặt đặt ở các trường riêng.',
      good: 'Trang hộ chiếu có ảnh. Phải đọc được họ tên, số hiệu, ngày cấp và nơi cấp. Bản scan trọn vẹn, không cắt mép, không loá.',
      weak: '«Hộ chiếu» — với mô tả như vậy AI sẽ chấp nhận gần như mọi tài liệu có ảnh.',
    },
    docFields: {
      title: 'Trường dữ liệu trích xuất',
      description:
        'Dữ liệu mà AI lấy ra từ tệp. Nếu trường bắt buộc để trống, tài liệu bị từ chối dù mọi thứ khác đều ổn.',
    },
    docThreshold: {
      title: 'Ngưỡng tin cậy và trạng thái',
      description:
        'Dưới ngưỡng, tài liệu nhận trạng thái «AI đã từ chối» và quản lý thấy lý do. Lãnh đạo có thể xác nhận thủ công bất chấp kết luận của AI.',
    },
    taskTypes: {
      title: 'Loại công việc',
      description:
        'Bản chất của công việc: cuộc gọi, liên hệ, cuộc họp. Loại là tuỳ chọn, nhưng công việc được đếm và danh sách được lọc theo nó. Loại hệ thống không thể xoá — chỉ có thể tắt, và nó vẫn giữ trên các công việc đã tạo.',
    },
  },
}

export const onboardingTourMessages = {
  ru: { common: { onboarding: ruOnboarding }, pipeline: { help: ruHelp } },
  en: { common: { onboarding: enOnboarding }, pipeline: { help: enHelp } },
  vn: { common: { onboarding: vnOnboarding }, pipeline: { help: vnHelp } },
}
